import { registerSuperProperties } from '../../utils/tracking/mixpanel'
import { MODULE_KEYS } from '../moduleRegistry'
import type { ModuleKey } from '../moduleRegistry'
import type { MatchScreenDatasetAttributes } from '../../context/MatchScreenContext'
import { buildPersonalizationContext } from './buildPersonalizationContext'
import { applyPersonalization } from './applyPersonalization'
import '../types'

/** The two modules whose configs may carry a personalization rules array. */
export const PERSONALIZED_MODULE_KEYS: ModuleKey[] = [MODULE_KEYS.heading, MODULE_KEYS.conciergeBanner]

// Mirrors useModuleConfig's parsing: module variables may arrive as objects or JSON strings.
const parseModuleVariable = (raw: unknown): Record<string, unknown> | undefined => {
  let parsed: unknown = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return undefined
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return undefined
  }
  return parsed as Record<string, unknown>
}

// Mirrors withModuleGuard exactly: a module is hidden when its key is absent or its RAW value
// is an object with `enabled === false`. The guard never parses strings, so a JSON-string
// variable renders even when its parsed content says enabled: false — the prop must agree with
// what the user actually sees.
const isHiddenByGuard = (variables: Record<string, unknown>, moduleKey: ModuleKey): boolean => {
  if (!(moduleKey in variables)) {
    return true
  }
  const raw = variables[moduleKey]
  return typeof raw === 'object' && raw !== null && (raw as Record<string, unknown>).enabled === false
}

/**
 * Registers the copy-group Mixpanel super properties
 * (`personalization_copy_<module key>` = matched rule name or 'none') for the heading and
 * concierge modules.
 *
 * Like `registerRedesignDecision`, this reads the window globals directly and must run at
 * bootstrap, before React mounts, so the session's very first events — including
 * `client-matches-domcontent-loaded`, which fires before any module effect — already carry the
 * properties. The evaluation is the same one `usePersonalizedModuleConfig` performs for
 * rendering: both call `buildPersonalizationContext`, so the two derivations cannot diverge.
 * Modules that `withModuleGuard` hides register 'none'.
 *
 * No-op on the legacy screen (redesign disabled), where the modules never render.
 */
export const registerPersonalizationCopy = (
  attributes: Pick<MatchScreenDatasetAttributes, 'selfSchedulingEnabled' | 'revisit'>
) => {
  const redesign = window.matchScreenRedesign
  if (!redesign?.enabled) {
    return
  }

  const context = buildPersonalizationContext({
    matches: window.matches,
    matchedSavings: window.matchedSavings,
    funnelAnswers: window.funnelAnswers,
    selfSchedulingEnabled: attributes.selfSchedulingEnabled,
    revisit: attributes.revisit
  })
  const variables = redesign.variables ?? {}

  PERSONALIZED_MODULE_KEYS.forEach((moduleKey) => {
    const config = isHiddenByGuard(variables, moduleKey)
      ? undefined
      : parseModuleVariable(variables[moduleKey])
    const matchedRuleName = config
      ? applyPersonalization(config, context).matchedRuleName
      : null
    registerSuperProperties({ [`personalization_copy_${moduleKey}`]: matchedRuleName ?? 'none' })
  })
}
